import { Card, Checkbox, CheckboxOnChangeData, Image } from '@fluentui/react-components';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useLineCardStyles } from './LineCard.styles';
import { useCart } from '../../../../contexts/CartContext/CartContext.hooks';
import ChangePrimaryLinePopup from '../../popups/ChangePrimaryLinePopup/ChangePrimaryLinePopup';
import LineCardHeader from './LineCardHeader/LineCardHeader';
import { ILineCardProps } from './LineCard.types';
import LineCardPricingSection from './LineCardPricingSection/LineCardPricingSection';
import { Config } from '../../../../config';
import DeleteLineItem from './DeleteLineItem/DeleteLineItem';
import ByodSection from './ByodSection/ByodSection';
import HorizontalLine from '../../../../components/core/HorizontalLine/HorizontalLine';
import { PlanType } from 'types/Plan';

const LineCard = (props: ILineCardProps) => {
    const { item, readonly } = props;

    const { cart, updateLineItems } = useCart();

    const styles = useLineCardStyles();

    const defaultImage = `${Config.url.STORAGE_URL}/data/default-phone.svg`;

    const [changePrimaryLinePopup, setChangePrimaryLinePopup] = useState(false);

    const image = useMemo(() => item.device?.image ?? defaultImage, [item.device?.image]);

    const disclaimer = useMemo(
        () => item.planSelection?.plan?.cartDisclaimer,
        [item.planSelection?.plan?.cartDisclaimer]
    );

    const isHomeInternet = useMemo(
        () => item.planSelection?.plan?.planType === PlanType.HomeInternet,
        [item.planSelection?.plan?.planType]
    );

    const updatePrimaryLines = (shouldChange: boolean) => {
        setChangePrimaryLinePopup(false);
        if (shouldChange) {
            const lineCart = cart.lineItems.map((x) => {
                x.isPrimaryLine = JSON.stringify(x) === JSON.stringify(item);
                return x;
            });
            updateLineItems(lineCart);
        }
    };

    const handlePrimaryLineCheckbox = useCallback(
        (event: ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
            if (data.checked as boolean) {
                const primaryLine = cart.lineItems.filter((x) => x.isPrimaryLine);
                if (primaryLine.length == 1) {
                    setChangePrimaryLinePopup(true);
                } else {
                    const lineCart = cart.lineItems.map((x) => {
                        if (JSON.stringify(x) === JSON.stringify(item)) x.isPrimaryLine = true;
                        return x;
                    });
                    updateLineItems(lineCart);
                }
            } else {
                const lineCart = cart.lineItems.map((x) => {
                    if (JSON.stringify(x) === JSON.stringify(item)) x.isPrimaryLine = false;
                    return x;
                });
                updateLineItems(lineCart);
            }
        },
        [cart]
    );

    return (
        <Card className={styles.root}>
            <div className={styles.contentWrapper}>
                <LineCardHeader readonly={readonly} item={item} />

                <HorizontalLine />

                <div className={styles.yaWrapper}>
                    {!isHomeInternet && (
                        <div className={styles.itemImageContainer}>
                            <Image
                                key={`${image}-${item.lineNumber}`}
                                alt={`${image}-${item.lineNumber}`}
                                src={image}
                                className={styles.itemImage}
                            />
                        </div>
                    )}

                    <div className={styles.detailsWrapper}>
                        <div className={styles.itemName}>{item.device?.product.baseInfo.name}</div>

                        {item.device?.colorName && item.device?.memoryOption && (
                            <div>
                                {item.device?.colorName}, {item.device?.memoryOption}
                                <HorizontalLine />
                            </div>
                        )}

                        {item.byod && <ByodSection byod={item.byod} />}

                        <LineCardPricingSection readonly={readonly} item={item} />
                    </div>
                </div>

                {!readonly && !isHomeInternet && (
                    <>
                        <Checkbox
                            data-testid='line-item-make-account-holder'
                            className={styles.accountHolderLineText}
                            label={'Make Account Holder Line'}
                            checked={item.isPrimaryLine}
                            onChange={handlePrimaryLineCheckbox}
                        />

                        <div className={styles.mobileTrashCan}>
                            <DeleteLineItem lineNumber={props.item.lineNumber} />
                            <div className={styles.removeLabel}>Remove from cart</div>
                        </div>

                        <ChangePrimaryLinePopup
                            showPopup={changePrimaryLinePopup}
                            onDialogClose={updatePrimaryLines}
                        />
                    </>
                )}
                {!!disclaimer && <div>{disclaimer}</div>}
            </div>
        </Card>
    );
};

export default LineCard;
